import { Show, createEffect, createSignal, For, Index } from "solid-js";
import {
  ReportAPI,
  getReport,
  generateReport,
  saveReport,
  getTemplate,
  downloadReportPDF,
} from "../api/reports";
import toast from "solid-toast";

export default function ReportModal(props: {
  sessionId: string;
  contactType: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [loading, setLoading] = createSignal(false);
  const [report, setReport] = createSignal<ReportAPI | null>(null);
  const [saving, setSaving] = createSignal(false);
  const [downloading, setDownloading] = createSignal(false);
  const [tpl, setTpl] = createSignal<any | null>(null);
  const [tab, setTab] = createSignal<"doc" | "json">("doc");
  const [content, setContent] = createSignal<any>({});
  const [contentJson, setContentJson] = createSignal<string>("{}");
  let stringifyTimer: any;
  let jsonParseTimer: any;
  let actionInputRefs: Record<string, HTMLInputElement | undefined> = {};
  let scrollContainerEl: HTMLDivElement | undefined;

  const isDev = () =>
    new URLSearchParams(window.location.search).get("dev") === "1";

  function getContent(): any {
    return content();
  }

  function setContentObj(next: any) {
    setContent(next);
    try {
      clearTimeout(stringifyTimer);
    } catch {}
    stringifyTimer = setTimeout(() => {
      try {
        setContentJson(JSON.stringify(next, null, 2));
      } catch {}
    }, 200);
  }

  function ensureSectionBody(obj: any, key: string, seed: string = "") {
    if (!obj.sections) obj.sections = {};
    if (!obj.sections[key]) obj.sections[key] = {};
    if (typeof obj.sections[key].body !== "string")
      obj.sections[key].body = seed;
  }

  function composeParagraph(
    tplKey: string,
    fields: Record<string, any>
  ): string {
    const f = fields || {};
    const parts: string[] = [];
    const clean = (s: string) => String(s || "").trim();
    const has = (v: any) => (typeof v === "string" ? clean(v) : v != null);

    switch ((tpl()?.key as string) || props.contactType) {
      case "Intake": {
        if (tplKey === "reporter") {
          const who = clean(f.reporterName);
          const phone = clean(f.reporterPhone);
          const addr = clean(f.reporterAddress);
          if (who) parts.push(`${who} provided the report.`);
          if (phone) parts.push(`Preferred contact: ${phone}.`);
          if (addr) parts.push(`Address on file: ${addr}.`);
        } else if (tplKey === "child") {
          const name = clean(f.childName);
          const age = clean(f.childAgeOrDob);
          const dis = clean(f.childDisabilities);
          if (name || age)
            parts.push([name, age && `(${age})`].filter(Boolean).join(" "));
          if (dis) parts.push(`Noted needs/disabilities: ${dis}.`);
        } else if (tplKey === "incident") {
          const when = clean(f.incidentDate);
          const where = clean(f.incidentLocation);
          const ap = clean(f.allegedPerpetrator || f.alleged_perpetrator);
          if (when || where)
            parts.push(
              `Incident occurred ${when || "(date unspecified)"}${
                where ? ` at ${where}` : ""
              }.`
            );
          if (ap) parts.push(`Alleged perpetrator: ${ap}.`);
        } else if (tplKey === "safety") {
          const danger = clean(f.immediateDanger);
          if (danger) parts.push(`Immediate safety: ${danger}.`);
        } else if (tplKey === "household") {
          const cg = clean(f.caregiverName);
          if (cg) parts.push(`Primary caregiver: ${cg}.`);
        } else if (tplKey === "jurisdiction") {
          const j = clean(f.jurisdictionDetermination);
          const where = clean(f.incidentLocation);
          if (j) parts.push(`Jurisdiction: ${j}.`);
          if (!j && where) parts.push(`Incident location: ${where}.`);
        }
        break;
      }
      case "SafetyCheck": {
        if (tplKey === "observations") {
          if (has(f.observations)) parts.push(clean(f.observations));
        } else if (tplKey === "threats") {
          if (has(f.safetyThreats)) parts.push(clean(f.safetyThreats));
        } else if (tplKey === "protective") {
          if (has(f.protectiveCapacities))
            parts.push(clean(f.protectiveCapacities));
        } else if (tplKey === "decision") {
          if (has(f.safetyDecision))
            parts.push(`Safety decision: ${clean(f.safetyDecision)}.`);
        }
        break;
      }
      case "PlacementUpdate": {
        if (tplKey === "reason" && has(f.placementReason))
          parts.push(clean(f.placementReason));
        if (tplKey === "wellbeing" && has(f.childWellbeing))
          parts.push(clean(f.childWellbeing));
        if (tplKey === "visitation" && has(f.visitation))
          parts.push(clean(f.visitation));
        if (tplKey === "services" && has(f.services))
          parts.push(clean(f.services));
        if (tplKey === "risks" && has(f.risks)) parts.push(clean(f.risks));
        break;
      }
      case "CasePlanReview": {
        if (tplKey === "goals" && has(f.goals)) parts.push(clean(f.goals));
        if (tplKey === "services" && has(f.services))
          parts.push(clean(f.services));
        if (tplKey === "progress" && has(f.progress))
          parts.push(clean(f.progress));
        if (tplKey === "barriers" && has(f.barriers))
          parts.push(clean(f.barriers));
        if (tplKey === "recommendations" && has(f.recommendations))
          parts.push(clean(f.recommendations));
        break;
      }
    }

    return parts.filter(Boolean).join(" ").trim();
  }

  function seedFromFields(tplKey: string, fields: Record<string, any>): string {
    switch (tplKey) {
      case "reporter": {
        const paragraph = composeParagraph(tplKey, fields);
        if (paragraph) return paragraph;
        const parts = [
          fields.reporterName,
          fields.reporterPhone,
          fields.reporterAddress,
        ]
          .filter(Boolean)
          .join("; ");
        return parts ? `Reporter: ${parts}.` : "";
      }
      case "child": {
        const paragraph = composeParagraph(tplKey, fields);
        if (paragraph) return paragraph;
        const parts = [
          fields.childName,
          fields.childAgeOrDob,
          fields.childDisabilities,
        ]
          .filter(Boolean)
          .join(", ");
        return parts ? `Child: ${parts}.` : "";
      }
      case "incident": {
        const paragraph = composeParagraph(tplKey, fields);
        if (paragraph) return paragraph;
        const parts = [fields.incidentDate, fields.incidentLocation]
          .filter(Boolean)
          .join(" @ ");
        return parts ? `Incident: ${parts}.` : "";
      }
      case "safety": {
        const paragraph = composeParagraph(tplKey, fields);
        if (paragraph) return paragraph;
        return fields.immediateDanger
          ? `Immediate safety: ${fields.immediateDanger}.`
          : "";
      }
      case "household": {
        const paragraph = composeParagraph(tplKey, fields);
        if (paragraph) return paragraph;
        return fields.caregiverName
          ? `Caregiver: ${fields.caregiverName}.`
          : "";
      }
      case "jurisdiction": {
        const paragraph = composeParagraph(tplKey, fields);
        if (paragraph) return paragraph;
        return fields.jurisdictionDetermination
          ? `Jurisdiction: ${fields.jurisdictionDetermination}.`
          : "";
      }
      default:
        return "";
    }
  }

  function computeMissing(
    fields: Record<string, any>,
    required: string[]
  ): string[] {
    const missing: string[] = [];
    for (const k of required || []) {
      const v = (fields && fields[k]) || "";
      if (!String(v || "").trim()) missing.push(k);
    }
    return missing;
  }

  async function ensureDraft() {
    setLoading(true);
    try {
      const template = await getTemplate(props.contactType || "Intake");
      setTpl(template);
      let r: ReportAPI;
      try {
        r = await getReport(props.sessionId);
      } catch {
        r = await generateReport(props.sessionId, {
          contactType: props.contactType,
          forceNewVersion: true,
        });
      }
      setReport(r);
      const contentObj = { ...(r.content || {}) } as any;
      const fields = contentObj.fields || {};
      const sections = Array.isArray(template?.sections)
        ? template.sections
        : [];
      sections.forEach((s: any) => {
        if (s.key === "header" || s.key === "actions" || s.key === "narrative")
          return;
        const seed = seedFromFields(s.key, fields);
        ensureSectionBody(contentObj, s.key, seed);
      });
      ensureSectionBody(
        contentObj,
        "narrative",
        contentObj.sections?.narrative?.body || ""
      );
      // Do not add frontend follow-ups; rely on insights + server-side de-duped follow-ups
      if (!contentObj.sections?.actions)
        contentObj.sections = {
          ...(contentObj.sections || {}),
          actions: contentObj.sections?.actions || { items: [] },
        };
      setContentObj(contentObj);
      try {
        setContentJson(JSON.stringify(contentObj, null, 2));
      } catch {}
    } catch (e: any) {
      toast.error(e.message || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }

  createEffect(() => {
    if (props.isOpen) ensureDraft();
  });

  async function onSave(status: "draft" | "final" = "draft") {
    const r = report();
    if (!r) return;
    setSaving(true);
    try {
      // Soft-warning only: allow Final even if some required fields are missing
      if (status === "final") {
        const t = tpl();
        const c = getContent();
        const req: string[] = Array.isArray(t?.requiredFields)
          ? (t.requiredFields as string[])
          : [];
        const missing: string[] = [];
        req.forEach((key) => {
          const v = (c.fields && c.fields[key]) || "";
          if (!String(v || "").trim()) missing.push(key);
        });
        if (missing.length) {
          try {
            toast(`Finalizing with missing fields: ${missing.join(", ")}.`);
          } catch {}
        }
      }
      const next = await saveReport(props.sessionId, r.id, {
        status,
        content: getContent(),
      });
      setReport(next);
      toast.success(status === "final" ? "Report marked final" : "Saved");
    } catch (e: any) {
      toast.error(e.message || "Failed to save report");
    } finally {
      setSaving(false);
    }
  }

  async function onDownloadPDF() {
    const r = report();
    if (!r) return;
    setDownloading(true);
    try {
      await downloadReportPDF(props.sessionId, r.id);
      toast.success("PDF downloaded successfully");
    } catch (e: any) {
      toast.error(e.message || "Failed to download PDF");
    } finally {
      setDownloading(false);
    }
  }

  function updateSectionBody(key: string, text: string) {
    const c = { ...(getContent() || {}) };
    ensureSectionBody(c, key, "");
    c.sections[key].body = text;
    setContentObj(c);
  }

  function updateActionById(id: string, text: string) {
    const c = { ...(getContent() || {}) };
    const prevScrollTop = scrollContainerEl?.scrollTop ?? null;
    const el = actionInputRefs[id];
    const selStart = el?.selectionStart ?? null;
    const selEnd = el?.selectionEnd ?? null;
    const items = (c.sections?.actions?.items || []).slice();
    const next = items.map((it: any) => (it.id === id ? { ...it, text } : it));
    c.sections.actions = { items: next };
    setContentObj(c);
    // Restore scroll, focus, and caret
    setTimeout(() => {
      if (prevScrollTop !== null && scrollContainerEl) {
        scrollContainerEl.scrollTop = prevScrollTop;
      }
      const target = actionInputRefs[id];
      if (target) {
        target.focus();
        if (selStart !== null && selEnd !== null) {
          try {
            target.setSelectionRange(selStart, selEnd);
          } catch {}
        }
      }
    }, 0);
  }

  function toggleActionById(id: string) {
    const c = { ...(getContent() || {}) };
    const prevScrollTop = scrollContainerEl?.scrollTop ?? null;
    const items = (c.sections?.actions?.items || []).slice();
    const next = items.map((it: any) =>
      it.id === id ? { ...it, done: !it.done } : it
    );
    c.sections.actions = { items: next };
    setContentObj(c);
    // Preserve scroll position after item moves between lists
    setTimeout(() => {
      if (prevScrollTop !== null && scrollContainerEl) {
        scrollContainerEl.scrollTop = prevScrollTop;
      }
    }, 0);
  }

  function removeActionById(id: string) {
    const c = { ...(getContent() || {}) };
    const prevScrollTop = scrollContainerEl?.scrollTop ?? null;
    const items = (c.sections?.actions?.items || []).slice();
    const next = items.filter((it: any) => it.id !== id);
    if (!c.sections) c.sections = {} as any;
    c.sections.actions = { items: next };
    setContentObj(c);
    // Preserve scroll position after removal
    setTimeout(() => {
      if (prevScrollTop !== null && scrollContainerEl) {
        scrollContainerEl.scrollTop = prevScrollTop;
      }
    }, 0);
  }

  function addAction() {
    const c = { ...(getContent() || {}) };
    const items = (c.sections?.actions?.items || []).slice();
    const newId = `n${Date.now()}`;
    items.push({ id: newId, text: "", done: false });
    if (!c.sections) c.sections = {};
    c.sections.actions = { items };
    setContentObj(c);
    // Focus newly added item
    setTimeout(() => {
      const el = actionInputRefs[newId];
      if (el) el.focus();
    }, 0);
  }

  return (
    <Show when={props.isOpen}>
      <div class="fixed inset-0 bg-black/40 z-[10000] flex items-center justify-center p-4">
        <div class="bg-white rounded-md shadow-xl w-[min(1100px,95vw)] max-h-[90vh] flex flex-col">
          <div class="flex items-center justify-between px-4 py-2 border-b">
            <h2 class="text-lg font-semibold">Report — {props.contactType}</h2>
            <div class="flex items-center gap-2">
              <button
                class="px-3 py-1 text-sm border rounded"
                onClick={() => onSave("draft")}
                disabled={saving() || loading()}
              >
                Save
              </button>
              <button
                class="px-3 py-1 text-sm border rounded"
                onClick={async () => {
                  try {
                    const ok = window.confirm(
                      "Regenerate a new version? Your current draft will remain as a previous version."
                    );
                    if (!ok) return;
                    setLoading(true);
                    const r = await generateReport(props.sessionId, {
                      contactType: props.contactType,
                      forceNewVersion: true,
                    });
                    setReport(r);
                    // Reflect regenerated content in both JSON and Document views
                    try {
                      setContentObj(r.content || {});
                    } catch {}
                    setContentJson(JSON.stringify(r.content || {}, null, 2));
                    toast.success("New version generated");
                  } catch (e: any) {
                    toast.error(e.message || "Failed to regenerate");
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={saving() || loading()}
              >
                Regenerate…
              </button>
              <button
                class="px-3 py-1 text-sm border rounded"
                onClick={() => onSave("final")}
                disabled={saving() || loading()}
              >
                Mark Final
              </button>
              <button
                class="px-3 py-1 text-sm border rounded"
                onClick={onDownloadPDF}
                disabled={downloading() || loading() || !report()}
                title="Download PDF"
              >
                {downloading() ? "Downloading..." : "📥 Download PDF"}
              </button>
              <button class="px-3 py-1 text-sm" onClick={props.onClose}>
                ✖ Close
              </button>
            </div>
          </div>
          <div class="p-3 text-xs text-gray-600 border-b">
            <Show when={report()}>
              <span>
                Status: <strong>{report()!.status}</strong> • Version{" "}
                {report()!.version}
              </span>
            </Show>
          </div>
          <div
            class="flex-1 overflow-auto p-4"
            ref={(el) => (scrollContainerEl = el)}
          >
            <Show when={!loading()} fallback={<div class="p-4">Loading…</div>}>
              <div class="flex items-center gap-3 mb-3">
                <button
                  class={`px-2 py-1 rounded text-sm ${
                    tab() === "doc" ? "bg-gray-200" : "border"
                  }`}
                  onClick={() => setTab("doc")}
                >
                  Document
                </button>
                <Show when={isDev()}>
                  <button
                    class={`px-2 py-1 rounded text-sm ${
                      tab() === "json" ? "bg-gray-200" : "border"
                    }`}
                    onClick={() => setTab("json")}
                  >
                    JSON
                  </button>
                </Show>
              </div>
              <Show when={tab() === "json"}>
                <textarea
                  class="w-full h-[60vh] font-mono text-sm border rounded p-2"
                  value={contentJson()}
                  onInput={(e) => setContentJson(e.currentTarget.value)}
                />
              </Show>
              <Show when={tab() === "doc"}>
                <div class="space-y-4">
                  <Show when={getContent()?.sections?.header}>
                    <div class="border rounded p-3">
                      <div class="text-sm text-gray-500">Case Header</div>
                      <div class="text-sm">
                        <strong>{getContent().sections.header.title}</strong> —{" "}
                        {getContent().sections.header.contactType}
                        <br />
                        {getContent().sections.header.sessionDate} • Lang:{" "}
                        {getContent().sections.header.languages}
                        <br />
                        Interviewer: {
                          getContent().sections.header.interviewer
                        }{" "}
                        • Respondent: {getContent().sections.header.respondent}
                      </div>
                    </div>
                  </Show>
                  <For
                    each={
                      (tpl()?.sections || []).filter(
                        (s: any) => s.key !== "header"
                      ) as any[]
                    }
                  >
                    {(s: any) => (
                      <div class="border rounded p-3">
                        <div class="text-sm font-semibold mb-1">{s.title}</div>
                        <Show when={s.key === "actions"}>
                          <div class="space-y-2">
                            <div class="text-[11px] text-gray-500 mb-1">
                              Tip: check an item to mark it done; it moves below
                              and can be unchecked to restore.
                            </div>
                            {(() => {
                              const all = (getContent()?.sections?.actions
                                ?.items || []) as any[];
                              const active = all.filter((a) => !a.done);
                              const done = all.filter((a) => !!a.done);
                              return (
                                <>
                                  <Index each={active as any[]}>
                                    {(it, idx) => (
                                      <div class="flex items-center gap-2">
                                        <input
                                          type="checkbox"
                                          checked={false}
                                          onInput={() =>
                                            toggleActionById(it().id)
                                          }
                                        />
                                        <input
                                          class="flex-1 border rounded px-2 py-1 text-sm"
                                          ref={(el) =>
                                            (actionInputRefs[it().id] = el)
                                          }
                                          value={it().text || ""}
                                          onInput={(e) =>
                                            updateActionById(
                                              it().id,
                                              e.currentTarget.value
                                            )
                                          }
                                        />
                                        <button
                                          class="px-1.5 py-0.5 text-[11px] text-gray-500 hover:text-gray-700"
                                          title="Remove"
                                          onClick={() =>
                                            removeActionById(it().id)
                                          }
                                        >
                                          ×
                                        </button>
                                      </div>
                                    )}
                                  </Index>
                                  <Show when={(done as any[]).length > 0}>
                                    <div class="mt-2 pt-2 border-t text-[11px] text-gray-500">
                                      Completed
                                    </div>
                                    <Index each={done as any[]}>
                                      {(it, idx) => (
                                        <div class="flex items-center gap-2 opacity-70">
                                          <input
                                            type="checkbox"
                                            checked={true}
                                            onInput={() =>
                                              toggleActionById(it().id)
                                            }
                                          />
                                          <input
                                            class="flex-1 border rounded px-2 py-1 text-sm line-through text-gray-500"
                                            ref={(el) =>
                                              (actionInputRefs[it().id] = el)
                                            }
                                            value={it().text || ""}
                                            onInput={(e) =>
                                              updateActionById(
                                                it().id,
                                                e.currentTarget.value
                                              )
                                            }
                                          />
                                          <button
                                            class="px-1.5 py-0.5 text-[11px] text-gray-500 hover:text-gray-700"
                                            title="Remove"
                                            onClick={() =>
                                              removeActionById(it().id)
                                            }
                                          >
                                            ×
                                          </button>
                                        </div>
                                      )}
                                    </Index>
                                  </Show>
                                </>
                              );
                            })()}
                            <button
                              class="text-sm text-blue-600"
                              onClick={addAction}
                            >
                              ＋ Add item
                            </button>
                          </div>
                        </Show>
                        <Show when={s.key !== "actions"}>
                          <textarea
                            class="w-full min-h-[96px] border rounded p-2 text-sm"
                            value={getContent()?.sections?.[s.key]?.body || ""}
                            onInput={(e) =>
                              updateSectionBody(s.key, e.currentTarget.value)
                            }
                          />
                        </Show>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </Show>
          </div>
        </div>
      </div>
    </Show>
  );
}
